import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { toast } from '@src/components/core/toaster';
import defaultClient from '@src/api/clients/default';

const STEPS = ['Vertical & Offer', 'Financing', 'Markets', 'Lead Delivery'];

function FlowProgressCard({ flow }) {
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>{flow.vertical_name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {flow.complete}/{flow.total} steps
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate" value={flow.progress}
          sx={{ height: 6, borderRadius: 3, mb: 1.5 }}
          color={flow.progress === 100 ? 'success' : 'primary'}
        />
        <Stack spacing={0.5}>
          {(flow.steps || []).map((s) => (
            <Stack key={s.key} direction="row" spacing={1} alignItems="center">
              {s.completed
                ? <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                : <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />}
              <Typography variant="body2" sx={{ fontSize: 13, color: s.completed ? 'text.disabled' : 'text.primary' }}>
                {s.title}
              </Typography>
              {s.owner === 'client' && !s.completed && (
                <Chip label="You" size="small" color="warning" variant="outlined" sx={{ fontSize: 9, height: 16 }} />
              )}
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function NewVerticalRequestPage() {
  const { clientSlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [flows, setFlows] = useState([]);
  const [verticals, setVerticals] = useState([]);
  // Verticals already delivering leads — no new-vertical flow for those
  const [liveVerticalIds, setLiveVerticalIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [submittedFlow, setSubmittedFlow] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  // Form
  const [verticalId, setVerticalId] = useState('');
  const [offer, setOffer] = useState('');
  const [financing, setFinancing] = useState('');
  const [noFinancing, setNoFinancing] = useState(false);
  const [marketsConfirmed, setMarketsConfirmed] = useState(true);
  const [marketsNote, setMarketsNote] = useState('');
  const [postingConfirmed, setPostingConfirmed] = useState(true);
  const [postingNote, setPostingNote] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [intakeRes, vertRes] = await Promise.all([
        defaultClient.request({ url: `clients/${clientSlug}/onboarding/vertical-intake/` }).catch(() => null),
        defaultClient.request({ url: 'dashboard/verticals/all-options/' }).catch(() => null),
      ]);
      setFlows(intakeRes?.data?.flows || []);
      setLiveVerticalIds(new Set(intakeRes?.data?.live_vertical_ids || []));
      setVerticals(vertRes?.data || []);
    } finally {
      setLoading(false);
    }
  }, [clientSlug]);

  useEffect(() => { loadData(); }, [loadData]);

  const existingFlowIds = new Set(flows.map((f) => f.vertical_id));
  const stepValid = [
    Boolean(verticalId && offer.trim()),
    Boolean(financing.trim() || noFinancing),
    marketsConfirmed || Boolean(marketsNote.trim()),
    postingConfirmed || Boolean(postingNote.trim()),
  ][activeStep];

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await defaultClient.request({
        url: `clients/${clientSlug}/onboarding/vertical-intake/`,
        method: 'POST',
        payload: {
          vertical_id: verticalId,
          offer: offer.trim(),
          financing_offers: noFinancing ? 'No financing offers' : financing.trim(),
          markets_confirmed: marketsConfirmed,
          markets_note: marketsNote.trim(),
          posting_confirmed: postingConfirmed,
          posting_note: postingNote.trim(),
        },
      });
      setSubmittedFlow(res?.data?.flow || null);
      toast.success('Request submitted — our team is on it!');
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  if (submittedFlow) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="success" sx={{ mb: 3 }}>
          <strong>{submittedFlow.vertical_name} is underway.</strong> New verticals
          typically launch within 7–10 business days. You can watch progress
          here any time.
        </Alert>
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>What happens next</Typography>
            <Stack spacing={1}>
              {[
                ['Days 1–3', 'We build the creative and landing page for this vertical.'],
                ['Days 3–7', 'You approve the creative; we build campaigns and load your market targeting.'],
                ['Days 7–10', 'Budget confirmed, campaigns launch, and your first leads arrive.'],
              ].map(([when, what]) => (
                <Stack key={when} direction="row" spacing={1.5} alignItems="baseline">
                  <Typography variant="caption" fontWeight={700} sx={{ minWidth: 72, color: 'primary.main' }}>{when}</Typography>
                  <Typography variant="body2" sx={{ fontSize: 13.5 }}>{what}</Typography>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
        <FlowProgressCard flow={submittedFlow} />
        <Button variant="outlined" onClick={() => { setSubmittedFlow(null); setActiveStep(0); setVerticalId(''); setOffer(''); setFinancing(''); setNoFinancing(false); }}>
          Add another vertical
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>Add a New Vertical</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Four quick questions and our team takes it from there. New verticals
        typically launch within 7–10 business days.
      </Typography>

      {flows.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>In progress</Typography>
          {flows.map((f) => <FlowProgressCard key={f.vertical_id} flow={f} />)}
        </Box>
      )}

      <Card variant="outlined">
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
          </Stepper>

          {activeStep === 0 && (
            <Stack spacing={2}>
              <TextField select label="Which vertical do you want to add?" value={verticalId}
                onChange={(e) => setVerticalId(e.target.value)} fullWidth>
                <MenuItem value="">Select...</MenuItem>
                {verticals.filter((v) => !existingFlowIds.has(v.id)).map((v) => (
                  <MenuItem key={v.id} value={v.id} disabled={liveVerticalIds.has(v.id)}>
                    {v.vertical}{liveVerticalIds.has(v.id) ? ' — already live' : ''}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="What offer do you want to run?" value={offer}
                onChange={(e) => setOffer(e.target.value)} fullWidth multiline minRows={2}
                placeholder='e.g., "$500 off a full replacement" or "Free estimate + 10% off"'
              />
            </Stack>
          )}

          {activeStep === 1 && (
            <Stack spacing={2}>
              <TextField
                label="Financing offers for this vertical" value={financing}
                onChange={(e) => setFinancing(e.target.value)} fullWidth multiline minRows={2}
                disabled={noFinancing}
                placeholder='e.g., "0% APR for 12 months" or "Payments from $99/mo"'
              />
              <FormControlLabel
                control={<Checkbox checked={noFinancing} onChange={(e) => setNoFinancing(e.target.checked)} />}
                label="We don't run financing offers for this vertical"
              />
            </Stack>
          )}

          {activeStep === 2 && (
            <Stack spacing={2}>
              <FormControlLabel
                control={<Checkbox checked={marketsConfirmed} onChange={(e) => setMarketsConfirmed(e.target.checked)} />}
                label="Target the same markets as my current service area"
              />
              <TextField
                label={marketsConfirmed ? 'Any adjustments? (optional)' : 'Which markets should this vertical target?'}
                value={marketsNote} onChange={(e) => setMarketsNote(e.target.value)}
                fullWidth multiline minRows={2}
                placeholder="e.g., add the north suburbs, exclude downtown ZIPs"
              />
            </Stack>
          )}

          {activeStep === 3 && (
            <Stack spacing={2}>
              <FormControlLabel
                control={<Checkbox checked={postingConfirmed} onChange={(e) => setPostingConfirmed(e.target.checked)} />}
                label="Send leads to the same CRM / system as my other verticals"
              />
              <TextField
                label={postingConfirmed ? 'Anything different for this vertical? (optional)' : 'Where should these leads go?'}
                value={postingNote} onChange={(e) => setPostingNote(e.target.value)}
                fullWidth multiline minRows={2}
                placeholder="e.g., different pipeline, different product codes, new webhook"
              />
            </Stack>
          )}

          <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
            <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)}>Back</Button>
            {activeStep < STEPS.length - 1 ? (
              <Button variant="contained" disabled={!stepValid} onClick={() => setActiveStep((s) => s + 1)}>
                Next
              </Button>
            ) : (
              <Button variant="contained" disabled={!stepValid || submitting} onClick={handleSubmit}>
                {submitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
}
